import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { customerService } from '../../services/api';
import {
	ArrowLeftIcon,
	UserIcon
} from '@heroicons/react/24/outline';

const CustomerEdit = () => {
	const { customerId } = useParams();
	const navigate = useNavigate();
	const [customer, setCustomer] = useState(null);
	const [formData, setFormData] = useState({
		                                         customer_name: '',
		                                         customer_email: '',
		                                         customer_username: ''
	                                         });
	const [loading, setLoading] = useState(true);
	const [submitLoading, setSubmitLoading] = useState(false);
	const [error, setError] = useState(null);

	useEffect(() => {
		const loadCustomer = async () => {
			try {
				setLoading(true);
				const customerData = await customerService.getCustomer(customerId);
				const info = customerData.data.customer_info;

				setCustomer(customerData.data);
				setFormData({
					            customer_name: info.customer_name || '',
					            customer_email: info.customer_email || '',
					            customer_username: info.customer_username || ''
				            });
				setError(null);
			} catch (err) {
				console.error('Error loading customer:', err);
				setError('Failed to load customer data');
				toast.error('Failed to load customer data');
			} finally {
				setLoading(false);
			}
		};

		if (customerId) {
			loadCustomer();
		}
	}, [customerId]);

	const handleInputChange = (field, value) => {
		setFormData(prev => ({
			...prev,
			[field]: value
		}));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSubmitLoading(true);

		try {
			// Basic validation
			if (!formData.customer_email) {
				throw new Error('Email is required');
			}

			// Email validation
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!emailRegex.test(formData.customer_email)) {
				throw new Error('Please enter a valid email address');
			}

			await customerService.updateCustomer(customerId, formData);
			toast.success('Customer updated successfully');
			navigate('/customers');
		} catch (error) {
			console.error('Error updating customer:', error);
			toast.error(error.response?.data?.message || error.message || 'Failed to update customer');
		} finally {
			setSubmitLoading(false);
		}
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md">
				<p className="font-bold">Error</p>
				<p>{error}</p>
			</div>
		);
	}

	if (!customer) {
		return (
			<div className="text-center py-12">
				<UserIcon className="mx-auto h-12 w-12 text-gray-400" />
				<h3 className="mt-2 text-sm font-medium text-gray-900">Customer not found</h3>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div className="flex items-center space-x-4">
					<Link
						to="/customers"
						className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
					>
						<ArrowLeftIcon className="h-4 w-4 mr-1" />
						Back to Customers
					</Link>
				</div>
			</div>

			<div className="max-w-2xl mx-auto">
				<div className="bg-white shadow rounded-lg">
					<div className="px-6 py-4 border-b border-gray-200">
						<div className="flex items-center space-x-3">
							<div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
								<UserIcon className="h-6 w-6 text-gray-600" />
							</div>
							<div>
								<h2 className="text-xl font-semibold text-gray-800">
									Edit Customer
								</h2>
								<p className="text-sm text-gray-500">
									Customer ID: {customerId}
								</p>
							</div>
						</div>
					</div>

					<form onSubmit={handleSubmit}>
						<div className="p-6 space-y-6">
							{/* Customer Information */}
							<div className="grid grid-cols-1 gap-6">
								<div className="space-y-2">
									<label htmlFor="customerName" className="block text-sm font-medium text-gray-700">
										Customer Name
									</label>
									<input
										id="customerName"
										type="text"
										className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
										value={formData.customer_name}
										onChange={(e) => handleInputChange('customer_name', e.target.value)}
										placeholder="Enter customer name"
									/>
								</div>

								<div className="space-y-2">
									<label htmlFor="customerEmail" className="block text-sm font-medium text-gray-700">
										Email Address *
									</label>
									<input
										id="customerEmail"
										type="email"
										required
										className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
										value={formData.customer_email}
										onChange={(e) => handleInputChange('customer_email', e.target.value)}
										placeholder="Enter email address"
									/>
								</div>

								<div className="space-y-2">
									<label htmlFor="customerUsername" className="block text-sm font-medium text-gray-700">
										Username
									</label>
									<input
										id="customerUsername"
										type="text"
										className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
										value={formData.customer_username}
										onChange={(e) => handleInputChange('customer_username', e.target.value)}
										placeholder="Enter username"
									/>
								</div>
							</div>

							{/* Customer Statistics (Read-only) */}
							<div className="border-t border-gray-200 pt-6">
								<h3 className="text-lg font-medium text-gray-900 mb-4">Customer Statistics</h3>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div className="bg-gray-50 p-4 rounded-md">
										<div className="text-sm text-gray-600">Total Orders</div>
										<div className="text-lg font-semibold text-gray-900">
											{customer.statistics?.total_orders || 0}
										</div>
									</div>
									<div className="bg-gray-50 p-4 rounded-md">
										<div className="text-sm text-gray-600">Total Spent</div>
										<div className="text-lg font-semibold text-gray-900">
											{new Intl.NumberFormat('en-US', {
												style: 'currency',
												currency: 'USD'
											}).format(customer.statistics?.total_spent || 0)}
										</div>
									</div>
									<div className="bg-gray-50 p-4 rounded-md">
										<div className="text-sm text-gray-600">Average Order Value</div>
										<div className="text-lg font-semibold text-gray-900">
											{new Intl.NumberFormat('en-US', {
												style: 'currency',
												currency: 'USD'
											}).format(customer.statistics?.average_order_value || 0)}
										</div>
									</div>
									<div className="bg-gray-50 p-4 rounded-md">
										<div className="text-sm text-gray-600">Last Order Date</div>
										<div className="text-lg font-semibold text-gray-900">
											{customer.statistics?.last_order_date
											 ? new Date(customer.statistics.last_order_date).toLocaleDateString()
											 : 'N/A'
											}
										</div>
									</div>
								</div>
							</div>

							{/* Warning Notice */}
							<div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
								<div className="flex">
									<div className="flex-shrink-0">
										<svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
											<path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
										</svg>
									</div>
									<div className="ml-3">
										<p className="text-sm text-yellow-700">
											<strong>Note:</strong> Changes to customer information will be applied to all existing and future orders for this customer.
										</p>
									</div>
								</div>
							</div>
						</div>

						<div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
							<div className="flex space-x-3">
								<Link
									to="/customers"
									className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
								>
									Cancel
								</Link>
								<Link
									to={`/customers/${customerId}/analytics`}
									className="px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-300 rounded-md hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
								>
									View Analytics
								</Link>
							</div>
							<button
								type="submit"
								className={`px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${submitLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
								disabled={submitLoading}
							>
								{submitLoading ? 'Saving...' : 'Save Changes'}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
};

export default CustomerEdit;
